import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActiveModal, TabChangeEvent } from '@healthcatalyst/cashmere';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { TenantScheduleJobService } from 'src/app/core/services/application/tenant-schedule-job.service';
import { TenantScheduleService } from 'src/app/core/services/application/tenant-schedule.service';
import { OktaAuthService } from 'src/app/core/services/http/okta-auth.service';
import { ToastNotificationService } from 'src/app/core/services/toast-notification.service';
import { SelectableItem } from 'src/app/shared/components/grid-bulk-actions/grid-bulk-actions.component';
import { scheduleTypes } from 'src/app/shared/constants/constants';
import { TenantSchedule } from 'src/app/shared/models/tenant-schedule';
import { SelectJobsService } from '../../../../../../shared/components/select-jobs-grid/select-jobs.service';
import { SelectableJob } from 'src/app/shared/components/select-jobs-grid/select-job.models';
import { SelectJobsGridComponent } from 'src/app/shared/components/select-jobs-grid/select-jobs-grid.component';
import BaseComponent from 'src/app/shared/components/base.component';
import { scheduleJobsModalGridSettingsKey } from 'src/app/shared/constants/grid-settings-storage-keys';
import { pluralize } from 'src/app/shared/utils/string.utils';
import { SelectJobsSummaryService } from 'src/app/shared/components/select-jobs-summary/select-jobs-summary.service';
import { TenantScheduleSummary } from 'src/app/shared/models/tenant-schedule-summary';

export interface EntityJobsListItemVM extends SelectableItem {
    ingestionId: number;
    entityName: string;
    job: string;
    activeFlag: boolean;
    rawMode: string;
    source: string;
    isHeaderRow: boolean;
    isEmptyRow: boolean;
}

export interface ScheduleJobsModalResponse {
    jobStatusChanged: boolean;
    jobsScheduled: SelectableJob[];
    newlyCreatedSchedule: TenantScheduleSummary;
}

@Component({
    selector: 'cdp-schedule-jobs-modal',
    templateUrl: './schedule-jobs-modal.component.html',
    styleUrls: ['./schedule-jobs-modal.component.scss'],
    standalone: false
})
export class ScheduleJobsModalComponent extends BaseComponent implements OnInit {
    constructor(
        private activeModal: ActiveModal,
        private fb: FormBuilder,
        private tenantScheduleService: TenantScheduleService,
        private selectableJobsService: SelectJobsService,
        private scheduleJobService: TenantScheduleJobService,
        private toast: ToastNotificationService,
        private selectJobsSummaryService: SelectJobsSummaryService,
        private oktaAuth: OktaAuthService
    ) {
        super(oktaAuth);
    }

    @ViewChild(SelectJobsGridComponent, { static: true }) public grid: SelectJobsGridComponent;
    onlySaveToMemory = false;
    tenantId: string;
    schedules: TenantSchedule[];
    saveInProgress = false;
    scheduleJobsForm: FormGroup;
    selectedTabIndex = 0;
    public jobs$ = new BehaviorSubject<SelectableJob[]>([]);
    anyJobStatusChanged = false;
    loadingJobs = false;
    loadingFailed = false;
    readonly gridMemoryKey = scheduleJobsModalGridSettingsKey;
    public get jobsToReassign(): number {
        return this.selectJobsSummaryService.getJobsToReassign(
            this.grid?.selectedJobs,
            this.scheduleJobsForm.value.scheduleId
        );
    }

    ngOnInit(): void {
        this.tenantId = this.activeModal.data.tenantId;
        this.schedules = this.activeModal.data.schedules;
        this.anyJobStatusChanged = false;
        this.onlySaveToMemory = this.activeModal.data.onlySaveToMemory;

        this.scheduleJobsForm = this.fb.group({
            scheduleId: new FormControl(),
            rawCron: new FormControl(),
            scheduleDescription: new FormControl(''),
            scheduleCron: new FormControl(),
            scheduleType: new FormControl(scheduleTypes.Existing)
        });
        this.loadJobs();
    }

    public loadJobs(): void {
        const modalData = this.activeModal.data;
        const entities = modalData?.entities;
        this.loadingJobs = true;
        this.loadingFailed = false;

        if (this.onlySaveToMemory) {
            const jobs = this.selectableJobsService.mapToViewModels(entities, modalData.sourceName, this.schedules);
            setTimeout(() => {
                this.jobs$.next(jobs);
            }, 0);
            this.loadingJobs = false;
            return;
        }

        if (entities) {
            this.selectableJobsService
                .getSelectableJobs(this.tenantId, modalData.sourceId, modalData.sourceName, entities)
                .subscribe({
                    next: (jobs: SelectableJob[]) => {
                        this.loadingJobs = false;
                        this.jobs$.next(jobs);
                    },
                    error: () => {
                        this.loadingFailed = true;
                        this.loadingJobs = false;
                    }
                });
        } else {
            this.selectableJobsService
                .getJobsUnderScheduleIds(this.tenantId, modalData.selectedSchedules, modalData.selectedJobs)
                .subscribe({
                    next: (jobs: SelectableJob[]) => {
                        this.loadingJobs = false;
                        this.jobs$.next(jobs);
                    },
                    error: () => {
                        this.loadingFailed = true;
                        this.loadingJobs = false;
                    }
                });
        }
    }

    onJobStatusChanged(): void {
        this.anyJobStatusChanged = true;
    }

    cancel(): void {
        this.activeModal.close({ jobStatusChanged: this.anyJobStatusChanged });
    }

    onSelectedTabChanged(event: TabChangeEvent): void {
        this.selectedTabIndex = event.index;
    }

    saveBtnTitle(): string {
        return !this.scheduleJobsForm.valid || !this.scheduleIsSelected
            ? 'Select a valid schedule'
            : this.grid?.selectedJobs?.length === 0
              ? 'Select jobs to add them to schedule'
              : 'Add jobs to schedule';
    }

    get scheduleIsSelected(): boolean {
        const formVal = this.scheduleJobsForm.value;
        return (
            (formVal.scheduleType === scheduleTypes.Existing && formVal.scheduleId) ||
            (formVal.scheduleType === scheduleTypes.New && formVal.scheduleCron)
        );
    }

    public async saveJobsToSched() {
        const scheduleId = this.scheduleJobsForm.value.scheduleId;
        const scheduleType = this.scheduleJobsForm.value.scheduleType;
        const jobs = this.grid.selectedJobs.filter(j => j.isSelected);
        const isNewSchedule = scheduleType === scheduleTypes.New;
        this.saveInProgress = true;

        if (isNewSchedule) {
            const scheduleRequest = <TenantScheduleSummary>{};
            scheduleRequest.tenantId = this.tenantId;
            scheduleRequest.description = this.scheduleJobsForm.value.scheduleDescription;
            scheduleRequest.cron = this.scheduleJobsForm.value.scheduleCron;
            scheduleRequest.activeFlag = true;
            scheduleRequest.updatedUser = this.oktaUser.username;
            try {
                const saveResponse = await lastValueFrom(
                    this.tenantScheduleService.saveTenantSchedule(scheduleRequest)
                );
                scheduleRequest.tenantScheduleId = saveResponse.id;
                this.saveJobs(saveResponse.id, jobs, scheduleRequest);
            } catch (e) {
                console.error('Error saving schedule', e);
                this.toast.showError(
                    'Unable to save schedule. Contact an admin if the problem continues.',
                    'Save Failed'
                );
                return;
            }
        } else {
            this.saveJobs(scheduleId, jobs);
        }
    }

    private saveJobs(scheduleId: number, jobs: SelectableJob[], newSched?: TenantScheduleSummary) {
        jobs.forEach(j => {
            j.tenantScheduleId = scheduleId;
        });
        if (this.onlySaveToMemory) {
            this.activeModal.close({
                jobStatusChanged: this.anyJobStatusChanged,
                jobsScheduled: jobs,
                newlyCreatedSchedule: newSched
            } as ScheduleJobsModalResponse);
        } else {
            this.scheduleJobService
                .saveTenantScheduleJobs(this.tenantId, scheduleId, jobs.map(j => j.ingestionId) as number[])
                .pipe(this.unsubscribeOnDestroy())
                .subscribe({
                    next: () => {
                        this.toast.showSuccess(
                            `Successfully scheduled ${jobs.length} ${pluralize('job', jobs.length)}.`,
                            `Scheduled ${pluralize('Job', jobs.length)}`
                        );
                        this.activeModal.close({
                            jobStatusChanged: this.anyJobStatusChanged,
                            jobsScheduled: jobs,
                            newlyCreatedSchedule: newSched
                        } as ScheduleJobsModalResponse);
                    },
                    error: () => {
                        this.toast.showError(
                            'Unable to schedule jobs. Contact an admin if the problem continues.',
                            'Scheduling Failed'
                        );
                    }
                });
        }
    }
}
